import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { listFiles } from "../../utils/driveClient";
import {
  stageLocalFile,
  driveFileToAttachment,
  totalAttachmentSize,
  ATTACHMENT_LIMIT_BYTES,
} from "../../utils/emailAttachments";

const FOLDER_MIME = "application/vnd.google-apps.folder";
const fmtSize = (n) =>
  !n ? "" : n > 1024 * 1024 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`;

// attachments / onChange lift state to EmailComposer.
export default function AttachmentPicker({ deal, attachments, onChange }) {
  const [showDrive, setShowDrive] = useState(false);
  const [driveFiles, setDriveFiles] = useState(null); // null = loading
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!showDrive || driveFiles !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const files = await listFiles(deal.driveFolderId);
        if (!cancelled) setDriveFiles((files || []).filter((f) => f.mimeType !== FOLDER_MIME));
      } catch (e) {
        console.error("Drive list failed:", e);
        if (!cancelled) setDriveFiles([]);
        toast.error("Couldn't load the deal's Drive folder.");
      }
    })();
    return () => { cancelled = true; };
  }, [showDrive, driveFiles, deal?.driveFolderId]);

  const add = (att) => {
    if (attachments.some((a) => (a.fileId && a.fileId === att.fileId) || (a.storagePath && a.storagePath === att.storagePath))) return;
    const next = [...attachments, att];
    if (totalAttachmentSize(next) > ATTACHMENT_LIMIT_BYTES) {
      toast.error("Attachments can't total more than 20 MB per email.");
      return;
    }
    onChange(next);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > ATTACHMENT_LIMIT_BYTES) {
      toast.error("That file is over 20 MB — too big to email.");
      return;
    }
    setUploading(true);
    try {
      add(await stageLocalFile(deal.id || deal.createdat, file));
    } catch (err) {
      console.error("Attachment upload failed:", err);
      toast.error("Upload failed — the email can still be sent without it.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {attachments.map((a, i) => (
          <span
            key={a.fileId || a.storagePath || i}
            className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full pl-3 pr-1.5 py-1 text-xs"
          >
            📎 {a.name} <span className="text-slate-400">{fmtSize(a.size)}</span>
            <button
              type="button"
              aria-label={`Remove ${a.name}`}
              onClick={() => onChange(attachments.filter((_, j) => j !== i))}
              className="w-4 h-4 rounded-full bg-slate-300 text-white leading-none"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        {deal?.driveFolderId && (
          <button
            type="button"
            onClick={() => setShowDrive((v) => !v)}
            className="text-xs font-semibold text-sky-700 border border-sky-200 bg-sky-50 rounded px-2.5 py-1"
          >
            📁 From deal documents
          </button>
        )}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="text-xs font-semibold text-slate-600 border border-slate-200 rounded px-2.5 py-1 disabled:opacity-50"
        >
          {uploading ? "Uploading…" : "💻 Upload a file"}
        </button>
        <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
      </div>
      {showDrive && (
        <div className="mt-2 border border-slate-200 rounded-lg max-h-48 overflow-auto">
          {driveFiles === null ? (
            <p className="text-xs text-slate-400 p-3">Loading deal documents…</p>
          ) : driveFiles.length === 0 ? (
            <p className="text-xs text-slate-400 p-3">No files in this deal's folder yet.</p>
          ) : (
            driveFiles.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => { add(driveFileToAttachment(f)); setShowDrive(false); }}
                className="w-full text-left text-xs px-3 py-2 hover:bg-slate-50 border-t first:border-t-0 border-slate-100"
              >
                📄 {f.name} <span className="text-slate-400">{fmtSize(Number(f.size))}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
